import {
  ChessMate,
  FloodIt,
  Flow,
  Nerdle,
  RushHour,
  TileTap,
  TwentyFour,
  WordLadder,
  Wordle,
  Two048,
  type MinigameId,
} from '@minigame-race/contracts';
import type { ComponentType } from 'react';
import { NerdleGame } from '../minigames/nerdle/Component.js';
import { TileTapGame } from '../minigames/tile-tap/Component.js';
import { WordLadderGame } from '../minigames/word-ladder/Component.js';
import { WordleGame } from '../minigames/wordle/Component.js';
import { ChessMateGame } from '../minigames/chess-mate/Component.js';
import { FlowGame } from '../minigames/flow/Component.js';
import { TwentyFourGame } from '../minigames/twenty-four/Component.js';
import { RushHourGame } from '../minigames/rush-hour/Component.js';
import { FloodItGame } from '../minigames/flood-it/Component.js';
import { Two048Game } from '../minigames/two048/Component.js';
import { ConnectFourGame } from '../minigames/connect-four/Component.js';
import { TicTacToeGame } from '../minigames/tic-tac-toe/Component.js';
import { BattleshipGame } from '../minigames/battleship/Component.js';
import { ReactionDuelGame } from '../minigames/reaction-duel/Component.js';
import { FindMatchGame } from '../minigames/find-match/Component.js';
import { FlappyGame } from '../minigames/flappy/Component.js';
import { SoccerGame } from '../minigames/soccer/Component.js';
import type { Connect4View, TicTacToeView, BattleshipView, ReactionDuelView, FindMatchView, SoccerView, FlappyDuelView } from '@minigame-race/contracts';
import type { MinigameProps } from './types.js';

/**
 * THE CLIENT PLUG POINT. The mirror of the server's registry.
 *
 * `Record<MinigameId, ...>` again makes it a compile error to add a minigame id
 * without shipping a component for it.
 */
export const MINIGAME_COMPONENTS: Record<MinigameId, ComponentType<MinigameProps>> = {
  'tile-tap': TileTapGame,
  wordle: WordleGame,
  nerdle: NerdleGame,
  'word-ladder': WordLadderGame,
  'chess-mate': ChessMateGame,
  flow: FlowGame,
  'twenty-four': TwentyFourGame,
  'rush-hour': RushHourGame,
  'flood-it': FloodItGame,
  two048: Two048Game,
  flappy: FlappyGame,
  'connect-four': ConnectFourGame,
  'tic-tac-toe': TicTacToeGame,
  battleship: BattleshipGame,
  'reaction-duel': ReactionDuelGame,
  'find-match': FindMatchGame,
  soccer: SoccerGame,
};

/**
 * 0..1 completion of the CURRENT segment, read from this player's own
 * authoritative view.
 *
 * The mirror of each engine's server-side `progress()`. The opponent's marker
 * gets its fraction from the server (which calls that method); ours has to
 * derive the same number locally, because the server only tells us about
 * ourselves through `minigame:state`.
 *
 * Keeping it here rather than in RaceScreen means the race view never learns
 * what a tile or a guess is — adding a minigame is still a registry line.
 */
export const MINIGAME_PROGRESS: Record<MinigameId, (view: unknown) => number> = {
  'tile-tap': (view) => {
    const v = view as Partial<TileTap.TileTapPrivateView> | null;
    if (!v || typeof v.cleared !== 'number' || typeof v.total !== 'number' || v.total === 0) return 0;
    return Math.min(1, v.cleared / v.total);
  },
  wordle: (view) => {
    const v = view as Partial<Wordle.WordlePrivateView> | null;
    if (!v) return 0;
    if (v.solved === true) return 1;
    if (typeof v.guessesUsed !== 'number') return 0;
    return Math.min(1, v.guessesUsed / Wordle.MAX_GUESSES);
  },
  nerdle: (view) => {
    const v = view as Partial<Nerdle.NerdlePrivateView> | null;
    if (!v) return 0;
    if (v.solved === true) return 1;
    if (typeof v.guessesUsed !== 'number') return 0;
    return Math.min(1, v.guessesUsed / Nerdle.MAX_GUESSES);
  },
  // The only game that reads progress straight off the view rather than deriving
  // it. Progress is a high-water mark of distance-to-target, and the client has
  // no distance table — deriving `1 - rungsRemaining/rungsAtStart` would make the
  // marker slide backwards whenever the player steps away.
  'word-ladder': (view) => {
    const v = view as Partial<WordLadder.WordLadderPrivateView> | null;
    if (!v || typeof v.progress !== 'number') return 0;
    return Math.min(1, Math.max(0, v.progress));
  },
  'chess-mate': (view) => {
    const v = view as Partial<ChessMate.ChessMatePrivateView> | null;
    if (!v) return 0;
    if (v.solved === true) return 1;
    return v.ply === 1 ? 0.5 : 0;
  },
  flow: (view) => {
    const v = view as Partial<Flow.FlowPrivateView> | null;
    if (!v) return 0;
    if (v.solved === true) return 1;
    if (typeof v.filledCells !== 'number' || typeof v.totalCells !== 'number' || v.totalCells === 0) return 0;
    return Math.min(0.99, v.filledCells / v.totalCells);
  },
  'twenty-four': (view) => {
    const v = view as Partial<TwentyFour.TwentyFourPrivateView> | null;
    return v?.solved === true ? 1 : 0;
  },
  // Reads progress straight off the view: it's a distance-to-solve fraction the
  // server computes from a BFS. The client has no board-solver of its own.
  'rush-hour': (view) => {
    const v = view as (Partial<RushHour.RushPrivateView> & { progress?: number }) | null;
    if (!v) return 0;
    if (v.solved === true) return 1;
    // privateView doesn't ship the distance table; fall back to a coarse
    // moves-vs-par estimate so the own-progress marker at least moves.
    if (typeof v.moves === 'number' && typeof v.par === 'number' && v.par > 0) {
      return Math.min(0.9, v.moves / (v.par * 1.5));
    }
    return 0;
  },
  'flood-it': (view) => {
    const v = view as Partial<FloodIt.FloodPrivateView> | null;
    if (!v) return 0;
    if (v.solved === true) return 1;
    if (!Array.isArray(v.captured) || v.captured.length === 0) return 0;
    const cap = v.captured.filter(Boolean).length;
    return Math.min(0.99, cap / v.captured.length);
  },
  two048: (view) => {
    const v = view as Partial<Two048.Two048PrivateView> | null;
    if (!v) return 0;
    if (v.solved === true) return 1;
    if (typeof v.best !== 'number' || v.best <= 0 || typeof v.target !== 'number') return 0;
    return Math.min(0.95, Math.log2(v.best) / Math.log2(v.target));
  },
  // Flappy is a duel decided by a single event (first crash): 0 until over, then 1.
  flappy: (view) => ((view as Partial<FlappyDuelView> | null)?.over ? 1 : 0),
  // Turn games are a single decisive event, not a fill-a-bar puzzle: the marker
  // is empty until the board is decided, then full. (Mirrors the server
  // adapter's progress(): 0 until over, 1 after.)
  'connect-four': (view) => ((view as Partial<Connect4View> | null)?.over ? 1 : 0),
  'tic-tac-toe': (view) => ((view as Partial<TicTacToeView> | null)?.over ? 1 : 0),
  battleship: (view) => ((view as Partial<BattleshipView> | null)?.over ? 1 : 0),
  // Reaction Duel: fraction of rounds played, so the marker creeps across the 3.
  'reaction-duel': (view) => {
    const v = view as Partial<ReactionDuelView> | null;
    if (!v) return 0;
    if (v.over) return 1;
    if (typeof v.round !== 'number' || typeof v.totalRounds !== 'number' || v.totalRounds === 0) return 0;
    return Math.min(0.99, v.round / v.totalRounds);
  },
  // Find Match: fraction of rounds played, so the marker creeps across the best-of-3.
  'find-match': (view) => {
    const v = view as Partial<FindMatchView> | null;
    if (!v) return 0;
    if (v.over) return 1;
    if (typeof v.round !== 'number' || typeof v.totalRounds !== 'number' || v.totalRounds === 0) return 0;
    return Math.min(0.99, v.round / v.totalRounds);
  },
  // Soccer: a single decisive event (first goal) — 0 until over, then full.
  soccer: (view) => ((view as Partial<SoccerView> | null)?.over ? 1 : 0),
};

/**
 * A subtle per-game accent (an "rgb" triple string), used only for an ambient
 * glow behind the board so consecutive segments feel visually distinct without
 * disturbing the lime/cyan "you vs rival" identity. Purely cosmetic.
 */
export const MINIGAME_ACCENT: Record<MinigameId, string> = {
  'tile-tap': '166,255,46',   // lime — pure speed
  wordle: '120,200,255',      // cool blue — letters
  nerdle: '120,200,255',
  'word-ladder': '120,200,255',
  'chess-mate': '230,230,240',// pale steel — chess
  flow: '255,120,200',        // pink — colourful pipes
  'twenty-four': '255,176,32',// amber — arithmetic
  'rush-hour': '255,90,80',   // red — the red block
  'flood-it': '180,120,255',  // violet — flooding colour
  two048: '255,200,80',       // gold — 2048 tile
  flappy: '255,210,90',       // warm yellow — the bird
  'connect-four': '90,180,255',
  'tic-tac-toe': '166,255,46',
  battleship: '60,150,220',   // navy — the sea
  'reaction-duel': '56,255,106', // green — the GO panel
  'find-match': '255,120,200',   // pink — playful spot-it
  soccer: '90,200,120',          // pitch green
};

/**
 * Display metadata, so no screen has to hardcode a game's name.
 *
 * `rules` is deliberately terse — it is read mid-race with a clock running, and
 * a wall of text is the same as no text. Goal first, then the two or three facts
 * a player actually needs to act.
 */
export const MINIGAME_META: Record<
  MinigameId,
  { title: string; subtitle: string; note?: string; goal: string; rules: readonly string[] }
> = {
  'tile-tap': {
    title: 'TILE TAP',
    subtitle: 'SPEED + PRECISION',
    goal: `Clear ${TileTap.TILE_COUNT} lit tiles as fast as you can.`,
    rules: [
      'Tap the tile that lights up. A new one lights immediately.',
      'Tapping a dark tile costs you time.',
      'No grace here — first to finish ends the round.',
    ],
  },
  wordle: {
    title: 'WORDLE',
    subtitle: 'SPEED + DEDUCTION',
    // Surfaced on the countdown screen so players learn this before the clock
    // starts, not after typing five letters into a four-letter grid.
    note: `WORD IS ${Wordle.WORD_LENGTHS.join(' OR ')} LETTERS`,
    goal: `Guess the hidden ${Wordle.WORD_LENGTHS.join('- or ')}-letter word.`,
    rules: [
      'Green = right letter, right spot. Yellow = right letter, wrong spot.',
      `You get ${Wordle.MAX_GUESSES} guesses. Each one after the first costs time.`,
      'A hint reveals one letter, for a time penalty.',
      'Not solving it costs far more than any hint.',
    ],
  },
  nerdle: {
    title: 'NERDLE',
    subtitle: 'SPEED + ARITHMETIC',
    note: `GUESS AN ${Nerdle.EQUATION_LENGTH}-CHARACTER EQUATION`,
    goal: `Guess the hidden ${Nerdle.EQUATION_LENGTH}-character equation.`,
    rules: [
      `Use ${Nerdle.DIGITS} and ${Nerdle.OPERATORS.join(' ')} ${Nerdle.EQUALS}`,
      'Your guess must be a TRUE equation, e.g. 12+34=46',
      'Green = right symbol, right spot. Yellow = right symbol, wrong spot.',
      'Standard order of operations. × and ÷ before + and −.',
    ],
  },
  'word-ladder': {
    title: 'WORD LADDER',
    subtitle: 'VOCABULARY + SEARCH',
    note: 'CHANGE ONE LETTER PER RUNG',
    goal: 'Walk from the start word to the target word.',
    rules: [
      'Change exactly ONE letter per step. PLUSH → BLUSH → BRUSH …',
      'Every rung must be a real, common word.',
      'A wrong word is free — it costs only the seconds you spent typing it.',
      'Getting closer is banked: the bar never slides back.',
    ],
  },
  'chess-mate': {
    title: 'MATE IN 2',
    subtitle: 'CHESS TACTICS',
    note: 'WHITE TO MOVE',
    goal: 'Find the move that forces checkmate within two moves.',
    rules: [
      'Tap a white piece, then tap where it goes.',
      'Your first move must force mate no matter how Black replies.',
      'Black then defends automatically — deliver the checkmate.',
      'A move that doesn’t force mate just costs a little time.',
    ],
  },
  flow: {
    title: 'FLOW',
    subtitle: 'CONNECT THE DOTS',
    note: 'FILL EVERY CELL',
    goal: 'Connect each pair of matching dots.',
    rules: [
      'Drag from a dot to its twin of the same colour.',
      'Paths cannot cross or overlap.',
      'Every cell must be filled to solve it.',
      'Drag a colour again to redo its path.',
    ],
  },
  'twenty-four': {
    title: 'MAKE THE NUMBER',
    subtitle: 'ARITHMETIC',
    note: 'USE EACH NUMBER ONCE',
    goal: 'Combine all the numbers to hit the target.',
    rules: [
      'Tap numbers and operators to build an expression.',
      'Use + − × ÷ and brackets. Each number exactly once.',
      'Example: 6 × (2 + 2) = 24',
      'A wrong answer costs a little time — try again.',
    ],
  },
  'rush-hour': {
    title: 'BLOCK ESCAPE',
    subtitle: 'SLIDING PUZZLE',
    note: 'FREE THE RED BLOCK',
    goal: 'Slide the blocks so the red block reaches the exit.',
    rules: [
      'Tap a block, then use the arrows to slide it.',
      'Blocks only move along their own direction.',
      'Get the red block out the right-hand exit.',
      'Fewer moves is better — going over par costs time.',
    ],
  },
  'flood-it': {
    title: 'FLOOD IT',
    subtitle: 'SPATIAL PLANNING',
    note: 'ONE COLOUR WINS',
    goal: 'Flood the whole board to a single colour.',
    rules: [
      'The top-left blob grows. Pick a colour to flood to it.',
      'It swallows any touching cells of that colour.',
      'Turn the entire board one colour to solve it.',
      'Stay near par — extra moves cost time.',
    ],
  },
  two048: {
    title: '2048',
    subtitle: 'SLIDE & MERGE',
    note: 'REACH THE TARGET',
    goal: 'Merge tiles until one reaches the target number.',
    rules: [
      'Swipe or use arrows to slide every tile one way.',
      'Two equal tiles that touch merge into their sum.',
      'A new tile appears after each move.',
      'Make the target tile before the board fills up.',
    ],
  },
  flappy: {
    title: 'FLAPPY',
    subtitle: 'NERVE + TIMING',
    note: 'DON’T HIT THE PIPES',
    goal: 'Fly through the pipes without crashing — outlast your rival.',
    rules: [
      'Tap (or press space) to flap upward. Gravity does the rest.',
      'You both fly the exact same course at the same time.',
      'The FIRST to crash loses. The survivor wins.',
      'Both still flying when time runs out? It’s a draw.',
    ],
  },
  'connect-four': {
    title: 'CONNECT FOUR',
    subtitle: 'HEAD TO HEAD',
    note: 'LINE UP FOUR',
    goal: 'Line up four of your discs before your rival does.',
    rules: [
      'You and your rival take turns dropping into a 5×4 grid.',
      'Discs fall to the lowest free slot in a column.',
      'Four in a row — any direction — wins the round.',
      'Dawdle on your turn and a move is played for you.',
    ],
  },
  'tic-tac-toe': {
    title: 'TIC-TAC-TOE',
    subtitle: 'HEAD TO HEAD',
    note: 'THREE IN A ROW',
    goal: 'Get three of your marks in a row before your rival.',
    rules: [
      'Take turns marking cells on a 3×3 grid.',
      'Three in a row — across, down, or diagonally — wins.',
      'A full board with no line is a draw (neither of you scores).',
      'Dawdle on your turn and a move is played for you.',
    ],
  },
  battleship: {
    title: 'BATTLESHIP',
    subtitle: 'HEAD TO HEAD',
    note: 'SINK THE FLEET',
    goal: 'Sink all of your rival’s ships before they sink yours.',
    rules: [
      'First, place your ships on your 6×6 grid.',
      'Then take turns firing at your rival’s hidden grid.',
      'Sink every enemy ship to win the round.',
      'Dawdle and your placement or shot is made for you.',
    ],
  },
  'reaction-duel': {
    title: 'REACTION DUEL',
    subtitle: 'HEAD TO HEAD',
    note: 'TAP ON GREEN',
    goal: 'Out-react your rival across 3 rounds.',
    rules: [
      'Wait for the panel to turn GREEN, then tap as fast as you can.',
      'Fastest reaction each round wins it. Best of 3 takes the match.',
      'Tap before green and you lose the round — don’t jump the gun.',
      'Your time is measured on your own device, so lag never counts against you.',
    ],
  },
  'find-match': {
    title: 'FIND MATCH',
    subtitle: 'HEAD TO HEAD',
    note: 'ONE SHARED SYMBOL',
    goal: 'Find the one symbol both cards share — before your rival.',
    rules: [
      'Two cards always share exactly one symbol.',
      'Tap that symbol first to win the round.',
      'Tap the WRONG symbol and you lose the round.',
      'Best of 3 rounds. Cards change instantly — stay sharp.',
    ],
  },
  soccer: {
    title: 'SOCCER',
    subtitle: 'HEAD TO HEAD',
    note: 'FLICK TO SCORE',
    goal: 'Flick your pieces to knock the ball into your rival’s goal.',
    rules: [
      'Take turns. Drag back from one of your 5 pieces and release to flick it.',
      'Bounce off walls and pieces to set up the ball.',
      'First to score a goal wins.',
      'Take longer than 5 seconds and you forfeit that turn.',
    ],
  },
};
